import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  toApiError,
  useApi,
  type OtaBookingRefundCategory,
  type OtaBookingRefundLedger,
} from '@/shared/api';
import type { OtaRefundDialogMode } from './OtaRefundDialog';
import { minorFromMajor } from '@/shared/lib/money';

export function useOtaRefundCommands(
  bookingId: string,
  ledger: OtaBookingRefundLedger | undefined,
) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<OtaRefundDialogMode>('refund');
  const [refundId, setRefundId] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState<OtaBookingRefundCategory>('GuestComplaint');
  const [reason, setReason] = useState('');
  const operationId = useRef<string | null>(null);
  const mutate = useMutation({
    mutationFn: async () => {
      if (!ledger) throw new Error('Refund-Kontext fehlt.');
      if (mode === 'compensation') {
        if (!refundId) throw new Error('Der zu kompensierende Erstattungseintrag fehlt.');
        operationId.current ??= crypto.randomUUID();
        return api.otaRefunds.compensate(bookingId, refundId, {
          operationId: operationId.current,
          reason: reason.trim(),
          expectedVersion: ledger.expectedVersion,
        });
      }
      const amountMinor = minorFromMajor(amount, ledger.currency);
      if (amountMinor === null || amountMinor <= 0) {
        throw new Error('Der Erstattungsbetrag ist für die Buchungswährung ungültig.');
      }
      operationId.current ??= crypto.randomUUID();
      return api.otaRefunds.create(bookingId, {
        operationId: operationId.current,
        amountMinor,
        currency: ledger.currency,
        category,
        reason: reason.trim(),
        expectedVersion: ledger.expectedVersion,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['ota-refunds', bookingId] });
      reset();
      setOpen(false);
      toast.success(mode === 'compensation' ? 'Kompensation erfasst' : 'Erstattung dokumentiert');
    },
    onError: async (error) => {
      toast.error(
        (await toApiError(error)).detail ??
          (mode === 'compensation'
            ? 'Die Kompensation konnte nicht erfasst werden.'
            : 'Die Erstattung konnte nicht dokumentiert werden.'),
      );
      operationId.current = null;
      await queryClient.invalidateQueries({ queryKey: ['ota-refunds', bookingId] });
    },
  });

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  const minor = ledger ? minorFromMajor(amount, ledger.currency) : null;
  const canSubmit =
    ledger !== undefined &&
    reason.trim().length > 0 &&
    !mutate.isPending &&
    (mode === 'compensation'
      ? refundId !== null
      : minor !== null && minor > 0 && minor <= ledger.remainingGrossMinor);

  function reset() {
    setMode('refund');
    setRefundId(null);
    setAmount('');
    setCategory('GuestComplaint');
    setReason('');
    operationId.current = null;
  }

  function amend(update: () => void) {
    operationId.current = null;
    update();
  }

  return {
    open,
    setOpen,
    mode,
    amount,
    category,
    reason,
    pending: mutate.isPending,
    canSubmit,
    submit: () => mutate.mutate(),
    startCompensation: (id: string) => {
      setMode('compensation');
      setRefundId(id);
      setOpen(true);
    },
    changeAmount: (value: string) => amend(() => setAmount(value)),
    changeCategory: (value: OtaBookingRefundCategory) => amend(() => setCategory(value)),
    changeReason: (value: string) => amend(() => setReason(value)),
  };
}
